import { Heading } from '@/components/heading';
import { Text } from '@/components/text';
import { ShipmentModel } from '@/lib/models/shipment/types';
import { formatDateTimeForUser } from '@/utils/format/date-time';
import { tv } from '@/utils/styles';

import { ShipmentEtaBadge } from '../eta-badge';

export interface ShipmentTimesProps {
  arrivalDates: {
    eta?: string | null;
    ata?: string | null;
  };
  departureDates: {
    etd?: string | null;
    atd?: string | null;
  };
  originalEta?: ShipmentModel['originalEta'];
  delay?: ShipmentModel['delay'];
  modeType?: ShipmentModel['type'];
}

const ShipmentTimes = ({ arrivalDates, departureDates, originalEta, delay, modeType }: ShipmentTimesProps) => {
  const { eta, ata } = arrivalDates;
  const { etd, atd } = departureDates;

  const departureItems = [
    atd && {
      label: 'Actual departure time (ATD)',
      value: formatDateTimeForUser(new Date(atd)),
    },
    // Only show ETD if ATD is not available
    !atd &&
      etd && {
        label: 'Estimated departure time (ETD)',
        value: formatDateTimeForUser(new Date(etd)),
      },
    !atd && !etd && { label: 'Departure time', value: 'Unknown' },
  ].filter(Boolean);

  const arrivalItems = [
    ata && { label: 'Actual arrival time (ATA)', value: formatDateTimeForUser(new Date(ata)) },
    // Only show ETA if ATA is not available
    !ata &&
      eta && {
        label: 'Estimated arrival time (ETA)',
        value: `Est. ${formatDateTimeForUser(new Date(eta))}`,
      },
    !ata && !eta && { label: 'Arrival time', value: 'Unknown' },
  ].filter(Boolean);

  const { timeItemWrapper, timeItem, text, textWrapper, heading, base } = styles();

  return (
    <div className={base()}>
      <ul className={timeItemWrapper()}>
        {departureItems.map((item, i) => {
          if (!item) return null;

          return (
            <li className={timeItem()} key={i}>
              <Heading as="h3" className={heading()}>
                {item.label}
              </Heading>
              <div className={textWrapper()}>
                <Text className={text()}>{item.value}</Text>
              </div>
            </li>
          );
        })}
        {arrivalItems.map((item, i) => {
          if (!item) return null;

          return (
            <li className={timeItem()} key={i}>
              <Heading as="h3" className={heading()}>
                {item.label}
              </Heading>
              <div className={textWrapper({ className: 'justify-end' })}>
                <Text className={text()}>{item.value}</Text>
                {Boolean(eta && !ata) && (
                  <ShipmentEtaBadge delay={delay} modeType={modeType} eta={eta} originalEta={originalEta} showOnTime />
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'flex justify-between',
    timeItemWrapper: 'flex w-full gap-2 max-sm:flex-col',
    timeItem: 'sm:last:ml-auto sm:last:text-right',
    heading: '!mb-1 !text-baseSm font-normal text-text-secondary',
    textWrapper: 'flex items-center gap-1',
    text: '!m-0 !text-baseSm font-semibold text-text-primary',
  },
});

export { ShipmentTimes };
